import { apiEndpoint } from '@/environment'
import {
  getPortfolioFromLocalStorage,
  markPortfolioDbSyncedInLocalStorage,
  setPortfolioToLocalStorage,
} from '@/localStorage'
import type { Portfolio, PortfolioPageData, RunToDatabase } from '@/types'
import { compress } from '@/utils'
import {
  createReadFromDatabase,
  createWriteToDatabase,
  getDatabaseFile,
} from '@/utils/database'
import { AuthTokenExpiredException, portfolioTimezone } from '@pkg/basic'
import {
  type Portfolio as PortfolioDatabase,
  insertPortfolio,
} from '@pkg/database-portfolio'
import { z } from 'zod'
import { migration } from './migration'

/** portfolio 作成 API のレスポンス schema。 */
const registerPortfolioResponseSchema = z.object({
  plan: z.union([z.literal('free'), z.literal('business')]),
})

export const registerNewPortfolio = async (params: {
  portfolioCount: number
  getToken: () => Promise<string | null>
}): Promise<PortfolioPageData> => {
  const { portfolioCount, getToken } = params

  // ID 生成
  const uuid = crypto.randomUUID().slice(0, 13).replace(/-/g, '')
  const name =
    portfolioCount === 0 ? 'Portfolio' : `Portfolio${portfolioCount + 1}`

  // portfolio 作成
  const currentLocalPortfolio = getPortfolioFromLocalStorage()
  setPortfolioToLocalStorage({
    uuid,
    name,
    updatedAt: 0,
    lastUsedAt: 0,
    archive: false,
    plan: 'free',
    isWriter: true,
    isOwner: true,
  })
  const readFromDatabase = createReadFromDatabase(uuid)
  const writeToDatabase = createWriteToDatabase(uuid)
  await migration({
    isInitialMigration: true,
    readFromDatabase,
    writeToDatabase,
  })
  await initPortfolio({ writeToDatabase, uuid, name })
  const file = await getDatabaseFile(uuid)
  setPortfolioToLocalStorage(currentLocalPortfolio)

  // portfolio 登録 API call
  // 圧縮 (gzip)
  const gzipFile = await compress(file)
  const body = new FormData()
  body.append('file', gzipFile)
  const timestamp = Date.now()
  const url = new URL('/v1/portfolio', apiEndpoint)
  url.searchParams.set('name', name)
  url.searchParams.set('id', uuid)
  url.searchParams.set('timestamp', '0')
  const token = await getToken()
  if (token === null) {
    throw new AuthTokenExpiredException()
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body,
  })
  if (!response.ok) {
    throw new Error(
      `Failed to create portfolio: ${response.status} ${response.statusText}`,
    )
  }
  const { plan } = registerPortfolioResponseSchema.parse(await response.json())
  markPortfolioDbSyncedInLocalStorage({
    portfolioId: uuid,
    syncedUpdatedAt: timestamp,
  })

  const portfolio: Portfolio = {
    uuid,
    name,
    updatedAt: timestamp,
    lastUsedAt: timestamp,
    archive: false,
    plan,
    isWriter: true,
    isOwner: true,
  }
  return {
    ...portfolio,
    readonly: false,
    readFromDatabase,
    writeToDatabase,
  }
}

const initPortfolio = async (params: {
  writeToDatabase: RunToDatabase
  uuid: string
  name: string
}) => {
  const { writeToDatabase, uuid, name } = params

  const portfolioLocale = createPortfolioLocaleJp()

  await writeToDatabase((database) =>
    insertPortfolio({
      database,
      data: {
        ...portfolioLocale,
        id: uuid,
        name,
        needPeriodEndEvaluation: false,
        isTxGenerated: true,
      },
    }),
  )
}

type PortfolioLocale = Pick<
  PortfolioDatabase,
  'fiat' | 'timezone' | 'startMonth' | 'costBasis'
>

const createPortfolioLocaleJp = (): PortfolioLocale => ({
  fiat: 'jpy',
  timezone: portfolioTimezone,
  startMonth: 1,
  costBasis: 'WACB',
})
